import useRouter from 'app/common/hooks/use-router';
import { useSelector } from 'react-redux';
import { getCurrentBranchName } from '../../../branches/selectors.js';
import { getCurrentProjectId } from '../../../project/selectors.js';
import { interpolate } from '../link/link/link-hook.js';

export type RightSideItem = {
  element: React.ReactNode;
  alwaysVisible?: boolean; // true = always show, false = show on active or hover
};

export type NavItemLinkOwnProps = {
  href: string;
  text?: React.ReactNode;
  icon?: React.ReactNode;
  rightSideItems?: RightSideItem[];
  activeOnlyIfExact?: boolean;
  onClick?: () => void;
};

const normalizePath = (path: string) => (path === '/' ? '/' : path.replace(/\/$/, ''));

export function matchesPath(currentPath: string, targetPath: string, activeOnlyIfExact: boolean): boolean {
  // Normalize paths by removing trailing slashes (except for root)
  const normalizedCurrent = normalizePath(currentPath);
  const normalizedTarget = normalizePath(targetPath);

  if (activeOnlyIfExact) {
    return normalizedCurrent === normalizedTarget;
  }

  // Don't allow empty target paths to match everything
  if (normalizedTarget === '' || normalizedTarget === '/') {
    return normalizedCurrent === normalizedTarget;
  }

  // Exact match after normalization
  if (normalizedCurrent === normalizedTarget) return true;

  // Prefix match: current should start with target + '/'
  return normalizedCurrent.startsWith(`${normalizedTarget}/`);
}

export const useNavItemLink = (props: NavItemLinkOwnProps) => {
  const { href, text, icon, rightSideItems = [], activeOnlyIfExact = false, onClick } = props;

  const router = useRouter();
  const projectId = useSelector(getCurrentProjectId);
  const branchName = useSelector(getCurrentBranchName);
  const interpolatedHref = interpolate(href, { projectId, branchName });
  const currentPath = router?.pathname || '';
  const isActive = matchesPath(currentPath, interpolatedHref, activeOnlyIfExact);

  return { interpolatedHref, text, icon, rightSideItems, isActive, onClick };
};

export type NavItemLinkProps = ReturnType<typeof useNavItemLink>;
